import { Link } from "react-router-dom";
import { ArrowUpRight, Info, Wallet } from "lucide-react";
import { Seo, breadcrumbLd } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { CtaBand } from "@/components/site/CtaBand";
import { useContent } from "@/lib/api";

const TYPES = [
  ["Monthly services", "Ongoing management, optimisation and reporting, billed monthly."],
  ["One-off services", "A single fee for a defined piece of work such as a website, audit or tracking setup."],
  ["Advertising budget", "What you spend with Google or Meta. Paid directly to the platform, separate from my fees."],
];
const GROUPS = ["Monthly Marketing Services", "One-Off Services"];

const PriceCard = ({ p, i }) => (
  <Reveal delay={(i % 3) * 0.06} className="h-full">
    <div data-testid={`price-card-${i}`} className={`flex h-full flex-col rounded-[28px] border p-7 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 ${i === 0 ? "border-transparent bg-[#2E1065] text-white" : "border-violet-200 bg-white hover:shadow-[0_30px_60px_-30px_rgba(46,16,101,.3)]"}`}>
      <span className={`self-start rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${i === 0 ? "bg-white/10 text-violet-100" : "bg-[#EDE9FE] text-[#581C87]"}`}>{p.fee_type}</span>
      <h2 className={`mt-5 text-xl font-extrabold ${i === 0 ? "text-white" : "text-[#13052A]"}`}>{p.name}</h2>
      <p className={`mt-4 text-2xl font-extrabold ${i === 0 ? "text-white" : "text-[#581C87]"}`} data-testid={`price-value-${i}`}>{p.price}</p>
      {p.setup && <p className={`mt-1 text-sm font-semibold ${i === 0 ? "text-violet-200" : "text-slate-500"}`}>{p.setup}</p>}
      <p className={`mt-4 text-sm leading-relaxed ${i === 0 ? "text-violet-100" : "text-slate-600"}`}>{p.notes}</p>
      {p.ad_spend && (
        <p className={`mt-4 flex items-start gap-2 rounded-xl p-3 text-xs font-semibold ${i === 0 ? "bg-white/10 text-violet-100" : "bg-[#F5F0FF] text-[#2E1065]"}`}>
          <Wallet className="mt-0.5 h-4 w-4 shrink-0" /> Advertising spend is payable separately and directly to the advertising platform.
        </p>
      )}
      <Link to={`/${p.service_slug}`} className={`mt-auto pt-6 text-sm font-bold ${i === 0 ? "text-white" : "text-[#581C87]"} inline-flex items-center gap-1.5 hover:underline`} data-testid={`price-service-link-${i}`}>
        About this service <ArrowUpRight className="h-4 w-4" />
      </Link>
    </div>
  </Reveal>
);

export default function Pricing() {
  const { items } = useContent("pricing");
  const crumbs = [["Home", "/"], ["Pricing", "/pricing"]];
  return (
    <>
      <Seo title="Pricing | Google Ads, SEO & Website Costs UK" path="/pricing"
        description="Clear starting prices in GBP for Google Ads management, Meta Ads, SEO, Google Business Profile, websites, hosting, tracking and audits. Ad spend is paid separately."
        jsonLd={[breadcrumbLd(crumbs)]} />
      <PageHero crumbs={crumbs} eyebrow="Pricing" lines={["Clear prices.", "No surprises."]}
        intro="Starting prices for each service, in GBP. Every business is different, so I'll always confirm a tailored quote before any work begins.">
        <Link to="/contact?type=quote" className="btn-primary" data-testid="pricing-hero-quote">Request a Custom Quote <ArrowUpRight className="h-4 w-4" /></Link>
      </PageHero>
      <section className="bg-white py-16">
        <div className="mx-auto grid max-w-7xl gap-4 px-5 sm:grid-cols-3 sm:px-8">
          {TYPES.map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.05} className="rounded-2xl bg-[#F5F0FF] p-5">
              <p className="text-sm font-extrabold text-[#13052A]">{t}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{d}</p>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="bg-white pb-24" data-testid="pricing-grid">
        {[...GROUPS, ...new Set(items.map((p) => p.group).filter((g) => g && !GROUPS.includes(g)))].map((g) => {
          const rows = items.filter((p) => (p.group || GROUPS[0]) === g);
          if (!rows.length) return null;
          const offset = items.indexOf(rows[0]);
          return (
            <div key={g} className="mx-auto max-w-7xl px-5 pt-10 sm:px-8" data-testid={`pricing-group-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <Reveal><h2 className="mb-6 text-2xl font-extrabold text-[#13052A] sm:text-3xl">{g}</h2></Reveal>
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {rows.map((p, j) => <PriceCard key={p.id || p.name} p={p} i={offset + j} />)}
              </div>
            </div>
          );
        })}
        <Reveal className="mx-auto mt-10 flex max-w-7xl items-start gap-3 px-5 text-sm leading-relaxed text-slate-600 sm:px-8">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#7C3AED]" />
          <p>Google and Meta advertising spend is paid directly to those platforms and is separate from management fees. Final pricing is confirmed in writing before work begins.</p>
        </Reveal>
      </section>
      <CtaBand title="Need something tailored?" text="Tell me what you need and I'll put together a custom quote." />
    </>
  );
}
